<template>
	<v-progress-circular v-if="loading" :style="{ margin: `${loadingMargin}px 0` }"
						 size="16" width="1" class="ml-0" color="secondary" indeterminate/>
	<div style="width: 100%; min-width: 0" :style="{ 'max-width': maxWidth }" class="d-flex flex-column"
		 v-else-if="name || meta.namespace">
		<div style="column-gap: 8px" class="d-flex align-center flex-wrap">
			<div v-if="!btnOnly" style="overflow: hidden; min-width: min(180px, 100%); max-width: 100%; flex: 1 1 min(180px, 100%)">
				<div v-if="$scopedSlots['label-append']" class="d-flex align-center flex-wrap" style="gap: 8px 12px">
					<rmp-label :label="label" :required="required"></rmp-label>
					<slot name="label-append"></slot>
				</div>
				<rmp-label v-else :label="label" :required="required"></rmp-label>
				<v-hover v-slot="{ hover }">
					<div class="d-flex align-center">
						<div :style="{
								cursor: id && downloadable ? 'pointer' : 'default'
							 }"
							 style="min-width: 0"
							 class="d-flex align-center overflow-hidden">
							<rmp-icon v-if="!noIcon && !downloading"
									  src="ico_file"
									  class="mr-2 transition flex-shrink-0">
							</rmp-icon>
							<div v-if="downloading" class="flex-shrink-0">
								<v-progress-circular :size="21" :width="3" class="mr-2" color="secondary" indeterminate/>
							</div>
							<rmp-text-btn class="text-body-2 pl-0 pr-0 text-no-wrap overflow-hidden text-left flex-shrink-1"
										  :class="[dense ? 'py-0' : 'py-2', !textWrap && 'rmp-file__truncate']"
										  style="max-width: 100%; min-width: 0;"
										  :style="textBtnStyle"
										  :text="displayFilename"
										  :title="filename"
										  :disabled="downloading"
										  @click="downloadable && download()">
							</rmp-text-btn>
						</div>
						<div v-if="previewAvailable" class="ml-2 flex-shrink-0">
							<rmp-btn color="secondary" icon="ico_eye" height="28" width="28" icon-size="20" small
									 :tooltip="$tc('buttons.preview')" @click="openPreview"/>
						</div>
						<rmp-btn v-if="deletable && !uploading" class="ml-auto" color="error" icon="ico_delete" height="28" width="28" icon-size="24" small
							   @click="$emit('delete', name || id)" :tooltip="$tc('buttons.delete')"/>
						<v-progress-circular v-if="uploading" class="ml-auto" color="secondary" indeterminate size="16" width="2"/>
					</div>
				</v-hover>
			</div>
			<div v-if="$slots['append']" :class="[$vuetify.breakpoint.lgAndUp ? 'ml-9' : 'ml-4', 'flex-shrink-0']">
				<slot name="append"></slot>
			</div>
			<div v-if="$slots['append-outer'] || downloadBtn" class="d-flex align-center flex-wrap rmp-file__actions"
				 style="column-gap: 8px; row-gap: 8px; min-width: 0; margin-left: auto">
				<rmp-btn outlined prepend-icon="ico_download" icon-class="mr-1" v-if="downloadBtn" @click="download" color="secondary" class="pl-3 pr-4" height="40" style="width: 108px">
					{{ $t("buttons.download") }}
				</rmp-btn>
				<slot name="append-outer"></slot>
			</div>
		</div>
		<div v-if="$slots['alert']" class="mt-2" style="width: 100%">
			<slot name="alert"></slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import { StorageController } from "@rmp/core/api/storage";
import RmpBtn from "@rmp/core/components/buttons/RmpBtn.vue";
import RmpLabel from "@rmp/core/components/fields/RmpLabel.vue";
import RmpIcon from "@rmp/core/components/icon/RmpIcon.vue";
import { FileMeta, FileMetaHelper } from "@rmp/core/types/storage/fileMeta";
import AbortService from "@rmp/core/services/abortService";
import { useFileDownload } from "@rmp/core/composables/useFileDownload";
import { computed, onBeforeMount, ref, useSlots } from "vue";
import RmpTextBtn from "@rmp/core/components/buttons/RmpTextBtn.vue";
import { useAppStore } from "@rmp/core/stores/appStore";
import { useFilePreviewStore } from "@rmp/core/stores/filePreview";

interface Props {
	deletable?: boolean,
	noIcon?: boolean,
	downloadable?: boolean,
	id?: string,
	name?: string,
	label?: string,
	uploading?: boolean,
	required?: boolean,
	downloadBtn?: boolean,
	btnOnly?: boolean,
	color?: string,
	maxWidth?: string,
	textWrap?: boolean,
	dense?: boolean,
	previewable?: boolean,
	subtitlePrefix?: string
}

const props = withDefaults(defineProps<Props>(), {
	downloadable: true,
	dense: false,
	name: "",
	maxWidth: "100%",
	previewable: true
});

const emits = defineEmits(["update:meta", "delete"]);

const abortService = new AbortService();
const storageController = new StorageController(abortService);

const loading = ref<boolean>(false);
const meta = ref<FileMeta>({} as FileMeta);

const { downloading, download: downloadFile } = useFileDownload();

const filename = computed(() => {
	if(props.name) return props.name;
	
	return meta.value.name;
});

const displayFilename = computed(() => {
	const name = filename.value || "";
	return name.length > 40 ? `${name.slice(0, 19)}...${name.slice(-18)}` : name;
});

onBeforeMount(() => {
	abortService.initialize();
	if(props.id && !props.name) getMeta();
});

const download = async () => {
	if(!props.id || !props.downloadable) return;

	await downloadFile(props.id, meta.value.name || props.name);
};
const getMeta = async () => {
	loading.value = true;
	
	try {
		const apiMeta = await storageController.getFileMeta(props.id as string);
		meta.value = FileMetaHelper.map(apiMeta);
		
		loading.value = false;
		
		emits("update:meta", meta.value);
	} catch (error) {
		useAppStore().handleActionError(error);
	}
};

const previewAvailable = computed(() => {
	return props.previewable && Boolean(props.id) && !props.uploading;
});

const slots = useSlots();
const loadingMargin = computed(() => {
	if(!props.dense || props.label || props.downloadBtn || props.btnOnly || slots.append || slots["append-outer"] || slots.alert) return 12;

	if(props.deletable && !props.uploading) return 8;

	return previewAvailable.value ? 6 : 4;
});

const openPreview = (event: MouseEvent) => {
	(event.target as Element)?.closest("button")?.blur();
	
	useFilePreviewStore().open({
		fileId: props.id as string,
		fileName: filename.value,
		documentLabel: props.label,
		subtitle: props.subtitlePrefix ? `${props.subtitlePrefix} - ${filename.value}` : undefined
	});
};

const textBtnStyle = computed(() => {
	const style = {
		pointerEvents: !props.downloadable ? "none" : "unset"
	};
	
	return props.textWrap ?
		Object.assign(style, {
			whiteSpace: "pre-wrap !important"
		}) : Object.assign(style, {
			textOverflow: "ellipsis",
			overflowWrap: "break-word",
			wordBreak: "break-all"
		});
	
	
});
</script>
<style scoped>
::v-deep .rmp-file__truncate .v-btn__content {
	display: block;
	overflow: inherit;
	text-overflow: inherit;
	white-space: inherit;
}
@media (max-width: 600px) {
	.rmp-file__actions {
		flex-shrink: 1;
		flex-basis: 100%;
	}
}
</style>
